import { useSessionStore } from "@auth/sessionStore";
import { PinnedPaper } from "@scribenote/brian-design-system";
import { isVisitCancelled } from "@visits/visitsClient";
import { useTodayVisits } from "@visits/visitsQueries";
import type { ReactNode } from "react";

import { useRoomsList } from "./roomsQueries";
import { RoomTile } from "./RoomTile";

/**
 * A lone paper note pinned to the board, carrying the empty states. Untilted
 * despite being decorative paper: when it shows it is the only thing on the
 * board, and it is there to be read.
 */
const PinnedNote = ({ children }: { children: ReactNode }) => (
  <PinnedPaper
    as="p"
    className="inline-block px-4 py-3 text-[0.8125rem] text-ink-body"
  >
    {children}
  </PinnedPaper>
);

/**
 * The clinic's rooms as paper cards pinned to the corkboard. The board is the
 * whole content window now (CompanionShell), so the cards and the empty-state
 * note pin straight to it and this component contributes no surface of its own.
 */
export const RoomsGrid = () => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data: rooms } = useRoomsList(sessionKey, true);
  const { data: visits } = useTodayVisits(sessionKey);

  if (rooms.length === 0)
    return <PinnedNote>No rooms set up in this clinic yet.</PinnedNote>;

  // Only surface rooms that currently hold a patient (WEB-3221); an empty room
  // adds no signal to the clinic board. Occupancy derives from today's visits by
  // current room, and must match useRoomOccupants (which each RoomTile renders
  // from) - so cancelled visits are excluded here too, else a room whose only
  // visit was cancelled would show a tile with no occupants. A room stays
  // reachable while empty via the header user-menu's Search action
  // (data-testid="header-search"), which opens SubjectSearchModal.
  const occupiedRoomIds = new Set(
    visits
      .filter((visit) => !isVisitCancelled(visit))
      .map((visit) => visit.currentRoomUuid),
  );
  const occupiedRooms = rooms.filter((room) => occupiedRoomIds.has(room.id));

  if (occupiedRooms.length === 0)
    return <PinnedNote>Every room is empty right now.</PinnedNote>;

  return (
    <div className="grid min-w-0 grid-cols-2 gap-4">
      {occupiedRooms.map((room) => (
        <RoomTile key={room.id} room={room} />
      ))}
    </div>
  );
};
